import { devices_status } from 'src/stores/device-runtime/status';
import {
	readDeviceStatus,
	readDeviceSettings,
} from 'src/stores/device-runtime/status-selectors';
import {
	format_gen2_addons_for_scenes,
	getAddonStatus,
	hasAddon,
} from 'src/helpers/addons/addon_helpers';
import {
	celsius_to_fahrenheit,
	createDeviceID,
	get_mode,
	is_gen_2,
	isBluRC,
	isBluTRV,
	isCameraDevice,
	isNoRelayPlug,
	isPill,
	isRgbwwPm,
	isWallDisplay,
	real_id,
	tmp_units_from_settings,
} from 'src/helpers/interface_helpers';
import { _deepClone } from 'src/helpers/utilities';
import {
	is_device_cond_ui,
	is_device_trigger_ui,
} from 'src/types/DeviceCondAndTriggerUI';
import type { Device } from 'src/types/Device';
import { normalizeDeviceChannelForCondition } from 'src/components/Scenes/helpers/device_channel_condition_helpers';
import { CAMERA_COND_NAMES } from './camera-scene-helpers';
import { CAMERA_MODE_MOTION_ZONES } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';

import { _init_device_condition } from '../../../../../../../helpers/init_device_conditions';
import { extract_gen_2_input_types } from '../../../../../../../helpers/input_btn_type_helpers';
import { devices_config } from '@devices/index';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { getExtSensorsSettings } from '../../../../../../../../../helpers/ext_sensor_helpers';
import { TEMPERATURE_SENSORS_IN_CELSIUS, BLE_BUTTON_MODELS } from 'src/constants';
import { init_virtual_device_conditions } from 'src/components/Scenes/helpers/virtual_components/virtual_device_conditions';
import { isBluTRVCalibrated } from 'src/helpers/virtual_helpers/blutrv_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { init_xt1_device_conditions } from 'src/components/Scenes/helpers/virtual_components/xt1_device_scenes_helpers';

// import { getAvaliableBleGateways } from 'src/helpers/ble_devices_helpers';

let $format = get(format);

export const format_title = (item) => {
	switch (item?.type) {
		case 'i18n_string_replace':
			//values contains props for translation
			let _obj = {};
			for (let [k, v] of Object.entries(item.values)) {
				_obj[k] = $format(v) || v;
			}
			return $format(item.title, { values: _obj });

		case 'i18n_num_replace':
			return $format(item.title, { values: item.values });

		default:
			return typeof item?.props?.sensor_name !== 'undefined'
				? $format(item?.title) + ` (${item.props.sensor_name})`
				: $format(item?.title, {
						values: { n: typeof item?.props?.add_info_ind },
					});
	}
};
/** selection list -  available conditons per device - Output state, Input state, Button pressed, Sensor detection (motion)*/
export function selectDeviceConditions(components) {
	if (components.length > 0) {
		return components.map((el) => {
			return {
				title: format_title(el),
				value:
					typeof el.props.add_info_ind !== 'undefined'
						? `${el.props.id}${el.props.add_info_ind}`
						: typeof el.props.input_index !== 'undefined'
							? `${el.props.id}${el.props.input_index}`
							: el.props.id,
				disabled: el?.props?.disabled || false,
			};
		});
	}
	return [];
}

/**
 * Restores device, property tokens and the selected condition when editing.
 * DeviceStep normally does this, but edit mode can jump straight to Device
 * properties, so that step never mounts.
 *
 * Only runs when no device is selected yet. After that the user's selection
 * (including a device change) must not be overwritten from `translated`.
 */
export function bootstrapEditedDeviceCondition(
	sceneData: any,
	devices: Record<string, Device>,
): { missingDevice?: { title: string; value: string | undefined; id: string } } {
	if (
		sceneData?.when_info?.mode !== 'edit' ||
		!(is_device_cond_ui(sceneData.translated) || is_device_trigger_ui(sceneData.translated))
	) {
		return {};
	}

	if (sceneData.selectedDevice?.id || sceneData.selectedDevice?.value) {
		return {};
	}

	let missingDevice: { title: string; value: string | undefined; id: string } | undefined;
	let device: Device | undefined;

	sceneData._device_condition = _deepClone(sceneData.translated);

	const baseId = real_id(sceneData._device_condition?.device?.id ?? '');
	const _device = devices?.[baseId];

	let deviceChannel = Number(sceneData._device_condition?.device?.channel);
	deviceChannel = normalizeDeviceChannelForCondition({
		deviceType: _device?.type,
		deviceChannel,
		condName: sceneData._device_condition?.cond_name ?? sceneData.translated?.cond_name,
	});

	const dev_id = createDeviceID(baseId, deviceChannel, _device?.type);
	device = devices[dev_id] ?? devices[baseId];

	if (typeof device !== 'undefined') {
		sceneData.selectedDevice = {
			title: device.name || 'unknown device',
			value: device?.id ?? dev_id,
			id: device?.id ?? dev_id,
		};

		sceneData.components = _init_conditions_components(
			device,
			readDeviceStatus(real_id(sceneData.selectedDevice.value)),
			readDeviceSettings(real_id(sceneData.selectedDevice.value)),
		);
	} else {
		missingDevice = {
			title: dev_id,
			value: dev_id || undefined,
			id: dev_id,
		};
		sceneData.selectedDevice = { ...missingDevice };
		sceneData.components = sceneData.components || [];
	}

	sceneData.devicePropsOptions = selectDeviceConditions(sceneData.components);

	const active_condition = sceneData.components.find((el) => {
		if (el.props.id === sceneData._device_condition.cond_name) {
			if (typeof sceneData._device_condition.add_info_ind !== 'undefined') {
				return sceneData._device_condition.add_info_ind === el.props.add_info_ind;
			}
			if (typeof sceneData._device_condition.input_index !== 'undefined') {
				return sceneData._device_condition.input_index === el.props.input_index;
			}

			return (
				typeof el.props.add_info_ind === 'undefined' &&
				typeof el.props.input_index === 'undefined'
			);
		}

		return false;
	});

	if (active_condition?.props?.type !== 'virtual_component') {
		if (active_condition?.props?.character?.match(/F/)) {
			sceneData._device_condition.value = celsius_to_fahrenheit(
				Number(sceneData._device_condition.value),
			).toString();
		}
	}

	if (active_condition) {
		sceneData.selectedDeviceProp = {
			title: format_title(active_condition) || 'unknown',
			value:
				typeof active_condition.props?.add_info_ind !== 'undefined'
					? `${active_condition.props.id}${active_condition.props.add_info_ind}`
					: typeof active_condition.props?.input_index !== 'undefined'
						? `${active_condition.props.id}${active_condition.props.input_index}`
						: active_condition.props?.id,
		};
		sceneData.extractPropsFromSelectedCond = active_condition.props || {};

		if (
			isCameraDevice(device) &&
			sceneData._device_condition?.cond_name === CAMERA_COND_NAMES.ZONE_MOTION
		) {
			sceneData.selectedDeviceProp = {
				title: $format('camera_motion_zones', { default: 'Motion zones' }),
				value: CAMERA_MODE_MOTION_ZONES,
			};
		}
	}

	return missingDevice ? { missingDevice } : {};
}

//---------- HELPERS ---------
const extract_gen_1_relay_btn_type = (settings, type, channel) => {
	switch (type) {
		case 'SHSW-L':
			const { btn1_type, btn2_type } = settings.relays[0];
			return [btn1_type, btn2_type];
		default:
			return [settings.relays[`${channel}`].btn_type];
	}
};

export const _init_conditions_components = (device, status, settings) => {
	const { id, category, mode } = device;

	let gen = device.gen;
	let btn_type;
	let addOns, meters, hasInputs, adc, extSwitch, hasIlluminance;
	let ch = id.split('_')[1] || 0;

	if (isXT1Module(device?.type)) {
		return init_xt1_device_conditions(device);
	}

	if (isNoRelayPlug(device?.type)) {
		return _init_device_condition.power_meter(ch);
	}

	switch (category) {
		case 'breaker':
			if (status !== undefined) {
				hasInputs = typeof status?.['cb:0'] !== 'undefined';
			}
			return _init_device_condition[category](device.type, gen, ch, hasInputs) || [];
		case 'lock':
		case 'pv':
			return _init_device_condition[category]();
		case 'relay':
			//because we don't know if gen is Number or String
			if (is_gen_2(device)) {
				if (status !== undefined) {
					meters = status[`switch:${ch}`]?.apower !== undefined ? true : false;
				} else {
					meters = false;
				}

				hasInputs = status ? Object.keys(status).some((_k) => _k.match(/input:/)) : false;
				btn_type = settings ? extract_gen_2_input_types(settings, device?.type, ch) : undefined;
				if (hasAddon(device)) {
					// let _addOns = getAddonStatus(device);
					addOns = format_gen2_addons_for_scenes(getAddonStatus(device)); // {};
				}
				if (typeof status?.['illuminance:0'] !== 'undefined') {
					hasIlluminance = true;
				}
			} else {
				addOns = {};
				addOns['temperature'] = getExtSensorsSettings(device, 'temperature');
				addOns['humidity'] = getExtSensorsSettings(device, 'humidity');

				meters = status?.meters;
				hasInputs = status?.inputs ? true : false;
				btn_type = settings
					? extract_gen_1_relay_btn_type(settings, device?.type, ch)
					: undefined;
				adc = settings?.adcs && ch === 0;
				extSwitch = settings?.ext_switch_enable ? settings?.ext_switch : undefined;
			}

			return (
				_init_device_condition[category](
					device.type,
					gen,
					ch,
					meters,
					hasInputs,
					btn_type,
					addOns,
					extSwitch,
					adc,
					hasIlluminance,
				) || []
			);

		case 'sensor':
			let observers = [];

			if (isPill(device.type)) {
				addOns = format_gen2_addons_for_scenes(getAddonStatus(device));
				return _init_device_condition['addons'](device.type, addOns);
			}

			const sensorFwVersion =
				device?.fwID ?? status?.[`fwversion:${device?.channel ?? ch ?? 0}`]?.version ?? '';

			return _init_device_condition[category](
				device.type,
				device.gen,
				observers,
				settings, // Pass settings for S4SN-0U61X zone detection
				device?.modeType, // Pass for dynamic ordering of conditions for SBDI-003E
				sensorFwVersion, // Pass FW version to gate light-level band conditions (FW-1690 / FE-1174)
			).map((component) => {
				if (component?.props?.id === 'temperature') {
					component.props.character = tmp_units_from_settings(
						!TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type) ? settings : device,
					);
				}
				return component;
			});

		case 'light':
			let btn_type_1, _mode;

			const deviceMode = device?.mode;

			if (gen == 1) {
				// gen 1 light bulbs do not have inputs (input components)
				// the check below will return undefined for missing button type props
				btn_type_1 = settings?.lights[`${ch}`]?.btn_type;

				_mode = '';
				if (typeof status !== 'undefined' && typeof settings !== 'undefined') {
					_mode = get_mode(device, status, settings);
				} else {
					_mode = Object.keys(devices_config[device.type].modes)[0];
				}

				return _init_device_condition[category](
					device.type,
					ch,
					gen,
					mode,
					btn_type_1 !== undefined ? [btn_type_1] : btn_type_1,
					addOns,
				);
			} else {
				//gen 2 lights, first start with Wall Dimmer US and prepare possible structure for other lights
				let support_inputs = status
					? Object.keys(status).some((_k) => _k.match(/input:/))
					: false;
				btn_type_1 =
					support_inputs && settings !== undefined
						? [settings[`input:${ch}`]?.type]
						: undefined;

				// check and update it for color bulbs or something like SHRGBW2 for gen 2
				//otherwise irrelevant what is device mode
				_mode = 'light';

				if (device.type === 'SNDC-0D4P10WW') {
					_mode = get_mode(device, status, settings);
					switch (_mode) {
						case 'light':
							btn_type_1 = [settings[`input:${device.channel || 0}`]?.type];
							break;
						case 'rgb':
						case 'rgbw':
							btn_type_1 = Object.entries(settings)
								.filter((set) => set[0].match(/input:/))
								.map((set) => {
									return set[1].type;
								});
							break;
						default:
							break;
					}
				}

				// find supported inputs for the given channel
				if (isRgbwwPm(device.type)) {
					switch (device.mode) {
						case 'light':
							btn_type_1 = [settings[`input:${device.channel || 0}`]?.type];
							break;
						case 'rgb':
						case 'cct':
							btn_type_1 = Object.entries(settings)
								.filter((set) => set[0].match(/input:/))
								.map((set) => {
									return set[1].type;
								});
							break;
						default:
							break;
					}
				} else if (support_inputs && settings !== undefined) {
					btn_type_1 = extract_gen_2_input_types(settings, device.type, ch);
				}

				let addOns;
				if (hasAddon(device)) {
					addOns = format_gen2_addons_for_scenes(getAddonStatus(device)); // {};
				}

				return _init_device_condition[category](
					device.type,
					ch,
					gen,
					_mode,
					btn_type_1,
					addOns,
					device?.rgbww_profile,
				);
			}
		case 'roller':
			if (hasAddon(device)) {
				let addOns: any = {};
				addOns = format_gen2_addons_for_scenes(getAddonStatus(device)); // {};
				return _init_device_condition[category](gen, device.type, addOns, device.channel);
			} else {
				return _init_device_condition[category](gen, device.type, {}, device.channel);
			}

		case 'emeter':
			let alert: boolean | string = false;
			if (device.type === 'SHEM-3') {
				let ts = 20211109;
				//20211109-131251/v1.11.7-g682a0db
				let fw = settings?.fw?.split('/')[0].split('-')[0];

				alert =
					Number(fw) <= ts
						? $format('required_firmware_alert', {
								values: { version: '20211109-131251/v1.11.7-g682a0db' },
							})
						: false;
			}
			if (hasAddon(device)) {
				let addOns: any = {};
				addOns = format_gen2_addons_for_scenes(getAddonStatus(device)); // {};

				return _init_device_condition[category](
					device.type,
					`${ch}`,
					alert,
					device.mode,
					addOns,
				);
			} else {
				return _init_device_condition[category](
					device.type,
					`${ch}`,
					alert,
					device.mode,
					undefined,
				);
			}

		case 'power_meter':
			return _init_device_condition[category](`${ch}`);

		case 'remote_control':
			if (isBluRC(device.type)) {
				return _init_device_condition[category](
					device.type,
					undefined,
					device.channel,
					'gble',
					undefined,
					[],
				);
			}

		case 'inputs_reader':
			// ble buttons
			if (BLE_BUTTON_MODELS.includes(device.type)) {
				// remove observers temporary
				// return _init_device_condition[category](
				// device.type, undefined, 0, 'gble', undefined, getAvaliableBleGateways()
				// );
				return _init_device_condition[category](
					device.type,
					undefined,
					device.channel,
					'gble',
					undefined,
					[],
				);
			}
			if (is_gen_2(device)) {
				if (settings !== undefined) {
					btn_type = settings[`input:${ch}`]?.type || undefined;
				} else {
					btn_type = undefined;
				}
				let addOns = format_gen2_addons_for_scenes(getAddonStatus(device));

				return _init_device_condition[category](device.type, btn_type, `${ch}`, gen, addOns);
			} else {
				return _init_device_condition[category](
					device.type,
					settings?.inputs?.[`${ch}`]?.btn_type,
					`${ch}`,
					gen,
				);
			}

		case 'thermostat':
			let _devices_status = {};
			let isCalibrated = false;
			if (isWallDisplay(device?.type)) {
				isCalibrated = true; //wall display does not have calibration
			} else if (isBluTRV(device?.type)) {
				isCalibrated = isBluTRVCalibrated(device);
			} else {
				_devices_status = get(devices_status);
				isCalibrated = _devices_status?.[real_id(id)]?.thermostats[0]?.pos !== -1;
			}

			return _init_device_condition[category](device.type, isCalibrated).map((component) => {
				if (
					component?.props?.id === 'temperature' ||
					component?.props?.id === 'trv_target_temperature'
				) {
					component.props.character = tmp_units_from_settings(
						!TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type) ? settings : device,
					);
				}
				return component;
			});

		case 'module':
			if (status !== undefined) {
				meters = status[`switch:${ch}`]?.apower !== undefined ? true : false;
			} else {
				meters = false;
			}

			hasInputs = status ? Object.keys(status).some((_k) => _k.match(/input:/)) : false;

			if (settings !== undefined) {
				btn_type = settings[`input:${ch}`]?.type || undefined;
			} else {
				btn_type = undefined;
			}

			if (hasAddon(device)) {
				addOns = format_gen2_addons_for_scenes(getAddonStatus(device));
			}

			return _init_device_condition[category](ch, meters, hasInputs, btn_type, mode, addOns) || [];
		case 'diffuser':
			return _init_device_condition[category](device.type) || [];
		case 'fan':
			return _init_device_condition[category]() || [];
		case 'virtual_device':
			return init_virtual_device_conditions(device, status, settings);
		case 'camera':
			return _init_device_condition.camera(settings) || [];
		default:
			return [];
	}
};
